import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { searchFirecrawl, type SearchHit } from "./firecrawl.server";
import { runBoost } from "./boost-run.server";
import { ENRICHMENT_STAGES } from "@/lib/enrichment-stages";
import { resolveProfile, nameSlugOverlap } from "./profile-provider.server";
import {
  isPersonLevelUrl,
  blobMentionsCompany,
  normalizeCompany,
  safeHost,
} from "./company-match";

/**
 * Hand-off point between the two halves of enrichment.
 *
 * Discovery (this file) only finds who the person is and which pages to read.
 * The heavy half — reading pages, news scan, context pass, photo — runs in its
 * own short request (`runContactScanStep`) so no single serverless call has to
 * carry minutes of scraping. The row stays `running` with the next stage
 * written, which is what keeps the progress strip moving.
 */
async function handOffToScan(supabase: any, userId: string, contactId: string) {
  await markEnrichment(supabase, userId, contactId, {
    status: "running",
    stage: ENRICHMENT_STAGES[2],
  });
}

/**
 * Fill role fields + the LinkedIn reference link from search metadata.
 * Scraped pages outrank this later; it exists so a person is never left with
 * a blank title just because their employer has no public bio page.
 */
async function applyResolvedProfile(
  supabase: any,
  userId: string,
  contactId: string,
  contact: { title?: string | null; company?: string | null },
  resolved: Awaited<ReturnType<typeof resolveProfile>>,
): Promise<{ titleSet: boolean; linkedinSet: boolean }> {
  const out = { titleSet: false, linkedinSet: false };
  try {
    const update: Record<string, string> = {};
    if (resolved.title && !contact.title) update["title"] = resolved.title;
    if (resolved.company && !contact.company) update["company"] = resolved.company;
    if (Object.keys(update).length) {
      await supabase.from("contacts").update(update).eq("id", contactId).eq("user_id", userId);
      out.titleSet = !!update["title"];
    }
    if (resolved.linkedinUrl) {
      const { data: have } = await supabase
        .from("contact_links")
        .select("url")
        .eq("contact_id", contactId);
      const exists = (have ?? []).some((l: any) =>
        String(l.url).toLowerCase().includes("linkedin.com/in/"),
      );
      if (!exists) {
        await supabase.from("contact_links").insert({
          user_id: userId,
          contact_id: contactId,
          kind: "linkedin",
          url: resolved.linkedinUrl,
          monitorable: false,
        });
        out.linkedinSet = true;
      }
    }
  } catch (e) {
    console.error("applyResolvedProfile failed", contactId, e);
  }
  return out;
}

/**
 * Stage 4 — scan for news right now instead of waiting for the nightly job.
 * Runs with admin privileges (companies / company_events are service-role
 * tables) and is always best-effort. Still used by `confirmMatch`, which is a
 * user-triggered single person rather than a batch.
 */
async function scanSignalsNow(supabase: any, userId: string, contactId: string) {
  try {
    await markEnrichment(supabase, userId, contactId, { stage: ENRICHMENT_STAGES[3] });
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Unified setup: read the person's own pages first, then news + synthesis.
    const { setupContactScan } = await import("@/lib/contact-setup.server");
    const stage = async (s: string) => {
      await markEnrichment(supabase, userId, contactId, { stage: s });
    };
    const r = await setupContactScan(supabaseAdmin as any, userId, contactId, "onboard", stage);
    return { ...r, signals: r.signals + r.linkSignals };
  } catch (e) {
    console.error("scanSignalsNow failed", e);
    return null;
  }
}

/**
 * Attach the employer's own site (and the person's page on it) before Boost.
 * Runs on every confidence path — a person you typed a company for should
 * never end up with only a LinkedIn URL.
 */
async function linkCompanyNow(userId: string, contactId: string) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { ensureCompanyLinks } = await import("@/lib/company-links.server");
    return await ensureCompanyLinks(supabaseAdmin as any, userId, contactId);
  } catch (e) {
    console.error("linkCompanyNow failed", contactId, e);
    return null;
  }
}

/** Persist the enrichment lifecycle so the UI can fill in progressively. */
async function markEnrichment(
  supabase: any,
  userId: string,
  contactId: string,
  patch: {
    status?: "queued" | "running" | "ready" | "partial" | "failed";
    stage?: string | null;
    start?: boolean;
    finish?: boolean;
  },
) {
  const update: Record<string, any> = {};
  if (patch.status) update["enrichment_status"] = patch.status;
  if (patch.stage !== undefined) update["enrichment_stage"] = patch.stage;
  if (patch.start) {
    update["enrichment_started_at"] = new Date().toISOString();
    update["enrichment_finished_at"] = null;
  }
  if (patch.finish) update["enrichment_finished_at"] = new Date().toISOString();
  try {
    await supabase.from("contacts").update(update).eq("id", contactId).eq("user_id", userId);
  } catch (e) {
    console.error("markEnrichment failed", e);
  }
}

const GENERIC_MAILBOX = new Set([
  "gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "live.com",
  "icloud.com", "me.com", "mac.com", "yahoo.com", "ymail.com", "aol.com",
  "proton.me", "protonmail.com", "pm.me", "fastmail.com", "msn.com",
  "duck.com", "duckduckgo.com",
]);

type LinkKind = "linkedin" | "website" | "company" | "twitter" | "other";

type Candidate = {
  kind: LinkKind;
  url: string;
  score: number;
  why: string;
  title?: string;
};

function classify(url: string): LinkKind {
  const h = safeHost(url) ?? "";
  if (h.endsWith("linkedin.com")) return "linkedin";
  if (h === "twitter.com" || h === "x.com") return "twitter";
  return "website";
}

function nameTokens(name: string): string[] {
  return name.toLowerCase().split(/\s+/).filter((t) => t.length >= 2);
}

/**
 * Build a fallback chain of search queries. Starts strict (quoted) and widens
 * (unquoted, first+last only) so typos and accidentally-appended company
 * tokens still match.
 */
function buildSearchQueries(
  name: string,
  company: string | null,
  emailDomain: string | null,
  groups: string[] = [],
): string[] {
  const tokens = name.split(/\s+/).filter(Boolean);
  const firstLast =
    tokens.length >= 3 ? `${tokens[0]} ${tokens[tokens.length - 1]}` : null;

  const qs: string[] = [
    `"${name}" site:linkedin.com/in`,
    `${name} site:linkedin.com/in`,
  ];
  if (firstLast) {
    qs.push(`"${firstLast}" site:linkedin.com/in`);
    qs.push(`${firstLast} site:linkedin.com/in`);
  }
  if (company) {
    qs.push(`"${name}" "${company}"`);
    qs.push(`${name} ${company}`);
    if (firstLast) qs.push(`${firstLast} ${company}`);
  }
  const groupCtx = groups.filter((g) => g && g.length <= 40).slice(0, 3);
  if (groupCtx.length) {
    const joined = groupCtx.join(" ");
    if (company) qs.push(`"${name}" "${company}" ${groupCtx[0]}`);
    qs.push(`"${name}" ${joined}`);
    if (firstLast) qs.push(`"${firstLast}" ${joined}`);
  }
  if (emailDomain) {
    qs.push(`"${name}" "${emailDomain}"`);
    qs.push(`${name} ${emailDomain}`);
  }
  // Dedupe while preserving order
  const seen = new Set<string>();
  return qs.filter((q) => (seen.has(q) ? false : (seen.add(q), true)));
}

/**
 * If the user typed something like "Walter Rosebrough steris" with no company
 * field, treat the trailing short alpha token as a soft company hint so it
 * boosts scoring — without overwriting the saved name.
 */
function inferCompanyHint(name: string, company: string | null): string | null {
  if (company) return company;
  const tokens = name.split(/\s+/).filter(Boolean);
  if (tokens.length < 3) return null;
  const last = tokens[tokens.length - 1];
  if (last.length > 12 || last.length < 2) return null;
  if (!/^[A-Za-z][A-Za-z.&-]*$/.test(last)) return null;
  return last;
}

/**
 * If the trailing token of `name` matches the inferred company hint, strip it.
 * Keeps queries/scoring focused on the real person name (e.g. "Amrit Ray Pfizer"
 * → "Amrit Ray" with company hint "Pfizer").
 */
function stripTrailingCompany(name: string, hint: string | null): string {
  if (!hint) return name;
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 3) return name;
  if (tokens[tokens.length - 1].toLowerCase() === hint.toLowerCase()) {
    return tokens.slice(0, -1).join(" ");
  }
  return name;
}

function scoreHit(
  hit: SearchHit,
  ctx: { name: string; emailDomain: string | null; company: string | null; groups?: string[] },
): Candidate | null {
  const host = safeHost(hit.url);
  if (!host) return null;
  const kind = classify(hit.url);
  const tokens = nameTokens(ctx.name);
  const blob = `${hit.title ?? ""} ${hit.description ?? ""}`.toLowerCase();

  let score = 0;
  const reasons: string[] = [];

  if (ctx.emailDomain && host === ctx.emailDomain) {
    score += 0.4;
    reasons.push(`matches email domain ${ctx.emailDomain}`);
  }
  if (ctx.company) {
    if (blobMentionsCompany(blob, host, ctx.company)) {
      score += 0.3;
      reasons.push(`mentions ${ctx.company}`);
    }
  }
  if (kind === "linkedin") {
    const m = hit.url.match(/linkedin\.com\/in\/([^/?#]+)/i);
    if (m) {
      const slug = m[1].toLowerCase();
      const hits = tokens.filter((t) => slug.includes(t)).length;
      if (hits >= 2) { score += 0.5; reasons.push("LinkedIn slug matches name"); }
      else if (hits === 1) { score += 0.15; reasons.push("LinkedIn slug includes name token"); }
    }
  }
  // Personal site heuristic: domain contains all name tokens
  if (kind === "website" && tokens.length && tokens.every((t) => host.includes(t))) {
    score += 0.25;
    reasons.push("personal-site domain");
  }
  // Title contains full name
  if (tokens.length >= 2 && tokens.every((t) => blob.includes(t))) {
    score += 0.15;
    reasons.push("title mentions full name");
  }
  // Group / tag context (e.g. "lawyer", "defense") biases toward the right
  // person when multiple people share the same name.
  if (ctx.groups?.length) {
    const matched: string[] = [];
    for (const g of ctx.groups) {
      const tok = g.toLowerCase().trim();
      if (tok.length >= 3 && blob.includes(tok)) matched.push(g);
    }
    if (matched.length) {
      score += Math.min(0.3, matched.length * 0.15);
      reasons.push(`matches groups: ${matched.join(", ")}`);
    }
  }

  if (score <= 0) return null;
  return { kind, url: hit.url, score: Math.min(1, score), why: reasons.join(" · "), title: hit.title };
}

function pickTopByKind(cands: Candidate[]): Candidate[] {
  const byKind = new Map<LinkKind, Candidate>();
  for (const c of cands) {
    const cur = byKind.get(c.kind);
    if (!cur || c.score > cur.score) byKind.set(c.kind, c);
  }
  return Array.from(byKind.values()).sort((a, b) => b.score - a.score);
}

export const autoDiscoverContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;

    const { data: contact } = await supabase
      .from("contacts")
      .select("id, name, email, company, title")
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!contact) throw new Error("Contact not found");

    await markEnrichment(supabase, userId, data.contactId, {
      status: "running",
      stage: ENRICHMENT_STAGES[0],
      start: true,
    });

    // Skip if already has links (already enriched/manual)
    const { count: linkCount } = await supabase
      .from("contact_links")
      .select("id", { count: "exact", head: true })
      .eq("contact_id", data.contactId);
    if ((linkCount ?? 0) > 0) {
      // Links already exist — skip search, but the scan step still has to run.
      await handOffToScan(supabase, userId, data.contactId);
      return { status: "skipped" as const, reason: "already-linked" };
    }

    // Pull the contact's group/category names — they bias both queries and
    // scoring so e.g. "lawyer" + "defense" pick the right Sean Matthews.
    const { data: groupRows } = await supabase
      .from("contact_groups")
      .select("category:categories(name)")
      .eq("contact_id", data.contactId)
      .eq("user_id", userId);
    const groups: string[] = (groupRows ?? [])
      .map((g: any) => g?.category?.name)
      .filter((n: any): n is string => typeof n === "string" && n.length > 0);

    const emailDomain = (() => {
      if (!contact.email) return null;
      const d = contact.email.split("@")[1]?.toLowerCase().trim();
      if (!d || GENERIC_MAILBOX.has(d)) return null;
      return d;
    })();

    const company = contact.company?.trim() || null;
    const rawName = contact.name.trim();
    const companyHint = inferCompanyHint(rawName, company);
    const name = stripTrailingCompany(rawName, company ? null : companyHint);
    const queries = buildSearchQueries(name, companyHint, emailDomain, groups);
    // Last-resort bare query (no site filter) so we still get web hits if
    // LinkedIn results come back empty.
    if (!queries.includes(name)) queries.push(name);

    // A cancelled tab must not leave identity discovery waiting through a long
    // serial fallback chain, but stopping at three LinkedIn-shaped queries meant
    // that when LinkedIn returned nothing we gave up before ever trying the
    // company-site or bare-name queries. Run a strong batch first, and only pay
    // for the wider batch when the first one found nothing person-level.
    const runBatch = async (qs: string[]): Promise<SearchHit[]> => {
      if (!qs.length) return [];
      const res = await Promise.allSettled(qs.map((q) => searchFirecrawl(q, 5)));
      return res.flatMap((r) => (r.status === "fulfilled" ? r.value : []));
    };
    let allHits = await runBatch(queries.slice(0, 3));
    if (!allHits.some((h) => isPersonLevelUrl(h.url))) {
      allHits = allHits.concat(await runBatch(queries.slice(3, 7)));
    }

    // Dedupe by URL
    const seen = new Set<string>();
    const uniqHits = allHits.filter((h) => (seen.has(h.url) ? false : (seen.add(h.url), true)));

    await markEnrichment(supabase, userId, data.contactId, { stage: ENRICHMENT_STAGES[1] });

    const scored = uniqHits
      .map((h) => scoreHit(h, { name, emailDomain, company: companyHint, groups }))
      .filter((c): c is Candidate => !!c);

    const top = pickTopByKind(scored);
    let confidence = Math.min(1, top.reduce((s, c) => s + c.score, 0));

    // Hard company gate: if the user typed a company, at least one *person-level*
    // candidate (their LinkedIn or own page) has to mention it. Press coverage
    // that merely names the firm doesn't count — that's how an Evercore ISI
    // story got read as evidence about a different Damien Fisher. No agreement
    // means we refuse the match outright rather than softening the score.
    let companyAgrees = true;
    if (company) {
      companyAgrees = top.some((c) => {
        if (!isPersonLevelUrl(c.url)) return false;
        const hit = uniqHits.find((h) => h.url === c.url);
        const blob = `${hit?.title ?? ""} ${hit?.description ?? ""} ${c.url}`;
        const host = safeHost(c.url) ?? "";
        return blobMentionsCompany(blob, host, company);
      });
      if (!companyAgrees) {
        console.info("auto-discover: company gate triggered", { name, company });
        confidence = Math.min(confidence, 0.5);
      }
    }

    /**
     * Identity is written only once we're confident, and never on the pending
     * path — a person we can't confirm keeps a clean card and the "help me
     * confirm" prompt instead of a stranger's job title.
     */
    const applyIdentity = async (): Promise<boolean> => {
      try {
        const resolved = await resolveProfile({ name, company, emailDomain, hits: uniqHits });
        if (resolved.source === "none" && !resolved.linkedinUrl) return false;
        const applied = await applyResolvedProfile(
          supabase,
          userId,
          data.contactId,
          { title: contact.title, company: contact.company },
          resolved,
        );
        return applied.titleSet || applied.linkedinSet;
      } catch (e) {
        console.error("resolveProfile failed", data.contactId, e);
        return false;
      }
    };

    console.info("auto-discover", { name, companyHint, emailDomain, queries, hits: uniqHits.length, confidence });

    if (top.length === 0 || confidence < 0.35 || !companyAgrees) {
      // Soft fallback: if we have at least one LinkedIn candidate whose slug
      // overlaps the name, persist the top picks as a `pending` match so the
      // user can confirm — better than an empty "none" with no signal.
      const softLinkedIn = top.find((c) => {
        if (c.kind !== "linkedin") return false;
        const m = c.url.match(/linkedin\.com\/in\/([^/?#]+)/i);
        if (!m) return false;
        const slug = m[1].toLowerCase();
        return nameTokens(name).some((t) => slug.includes(t));
      });
      if (softLinkedIn && top.length > 0) {
        await supabase.from("contact_match_candidates").upsert(
          {
            contact_id: data.contactId,
            user_id: userId,
            candidates: top as any,
            confidence,
            created_at: new Date().toISOString(),
          },
          { onConflict: "contact_id" },
        );
        await linkCompanyNow(userId, data.contactId);
        await handOffToScan(supabase, userId, data.contactId);
        return { status: "pending" as const, confidence, candidates: top };
      }
      // No links found — still seed contact_context from the typed onboarding metadata
      // so "What Pyng knows" is never empty. Identity is only filled in when the
      // typed company agrees; otherwise the card stays clean.
      const resolvedTitleSet = companyAgrees ? await applyIdentity() : false;
      await supabase.from("contact_match_candidates").delete().eq("contact_id", data.contactId);
      await linkCompanyNow(userId, data.contactId);
      await handOffToScan(supabase, userId, data.contactId);
      return {
        status: resolvedTitleSet ? ("linked" as const) : ("none" as const),
        confidence,
      };
    }

    if (confidence >= 0.8) {
      await applyIdentity();
      // Auto-apply links and boost
      await supabase.from("contact_links").insert(
        top.map((c) => ({
          user_id: userId,
          contact_id: data.contactId,
          kind: c.kind,
          url: c.url,
          monitorable: c.kind !== "linkedin",
        })),
      );
      await supabase.from("contact_match_candidates").delete().eq("contact_id", data.contactId);
      await linkCompanyNow(userId, data.contactId);
      await handOffToScan(supabase, userId, data.contactId);
      return {
        status: "linked" as const,
        confidence,
        applied: top,
      };
    }

    // Medium → persist for confirmation
    await supabase.from("contact_match_candidates").upsert(
      {
        contact_id: data.contactId,
        user_id: userId,
        candidates: top as any,
        confidence,
        created_at: new Date().toISOString(),
      },
      { onConflict: "contact_id" },
    );
    // Even before the user confirms a link match, the scan step seeds context
    // from typed metadata so the contact card is populated immediately.
    await linkCompanyNow(userId, data.contactId);
    await handOffToScan(supabase, userId, data.contactId);
    return { status: "pending" as const, confidence, candidates: top };
  });

export const getMatchCandidates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ contactId: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase
      .from("contact_match_candidates")
      .select("candidates, confidence, created_at")
      .eq("contact_id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();
    return row ?? null;
  });

export const confirmMatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z.object({
      contactId: z.string().uuid(),
      accept: z.boolean(),
    }).parse(i),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: row } = await supabase
      .from("contact_match_candidates")
      .select("candidates")
      .eq("contact_id", data.contactId)
      .eq("user_id", userId)
      .maybeSingle();

    await supabase
      .from("contact_match_candidates")
      .delete()
      .eq("contact_id", data.contactId)
      .eq("user_id", userId);

    if (!data.accept || !row?.candidates) return { status: "rejected" as const };

    const cands = row.candidates as Array<{ kind: LinkKind; url: string }>;
    if (cands.length) {
      await supabase.from("contact_links").insert(
        cands.map((c) => ({
          user_id: userId,
          contact_id: data.contactId,
          kind: c.kind,
          url: c.url,
          monitorable: c.kind !== "linkedin",
        })),
      );
    }
    let boostResult: any = null;
    let boostError: string | null = null;
    try {
      await linkCompanyNow(userId, data.contactId);
      boostResult = await runBoost(supabase, userId, data.contactId);
    } catch (e: any) {
      console.error("confirm-boost failed", e);
      boostError = e?.message ?? "Boost failed";
    }
    // Confirming a match used to stop at Boost, so a person the user just
    // verified could sit with links that were never read and no signals.
    const scan = await scanSignalsNow(supabase, userId, data.contactId);
    await markEnrichment(supabase, userId, data.contactId, {
      status: ((scan?.signals ?? 0) + (scan?.opportunities ?? 0)) > 0 ? "ready" : "partial",
      stage: null,
      finish: true,
    });
    return {
      status: "applied" as const,
      count: cands.length,
      scan,
      boost: {
        sources_used: (boostResult?.sources_used as string[] | undefined) ?? [],
        facts_added: (boostResult?.facts_added as number | undefined) ?? 0,
        fields_updated: (boostResult?.fields_updated as string[] | undefined) ?? [],
        error: boostError,
      },
    };
  });

// ============================================================================
// Preview enrichment — used by Add People review screen BEFORE contact saved.
// Runs Firecrawl search, scores, then LLM-extracts City/Title/Company/oneLiner
// from the top candidate's snippet. No DB writes.
// ============================================================================

const GATEWAY = "https://ai.gateway.lovable.dev/v1/chat/completions";
const MODEL = "google/gemini-3-flash-preview";

async function aiExtractPreview(
  name: string,
  hits: SearchHit[],
  ctx: { company?: string | null; emailDomain?: string | null },
): Promise<{
  title?: string;
  company?: string;
  city?: string;
  oneLiner?: string;
}> {
  const key = process.env.LOVABLE_API_KEY;
  if (!key) return {};
  const snippets = hits
    .slice(0, 6)
    .map((h, i) => `[${i + 1}] ${h.url}\n${h.title ?? ""}\n${h.description ?? ""}`)
    .join("\n\n");
  const system = [
    "You identify a specific person from search snippets and extract only confidently supported facts.",
    "Return strict JSON: { title, company, city, oneLiner }.",
    "Rules:",
    "- Only fill a field if the snippets clearly point to ONE person matching the given name (and company/email-domain when provided).",
    "- Leave a field as empty string if uncertain. NEVER invent.",
    "- city: just 'City' or 'City, Region' (e.g. 'Miami', 'New York', 'London, UK'). No country alone.",
    "- title: their current/most-recent role only.",
    "- company: current employer.",
    "- oneLiner: ONE warm sentence under 90 chars that would make a friend say 'yes that's them' — e.g. 'Head of Capital Formation at HIG Capital, based in Miami'. No emojis. No quotes.",
  ].join("\n");
  const user = JSON.stringify({
    name,
    hint_company: ctx.company ?? null,
    hint_email_domain: ctx.emailDomain ?? null,
    snippets,
  });
  try {
    const res = await fetch(GATEWAY, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Lovable-API-Key": key },
      body: JSON.stringify({
        model: MODEL,
        messages: [
          { role: "system", content: system },
          { role: "user", content: user },
        ],
        response_format: { type: "json_object" },
      }),
    });
    if (!res.ok) return {};
    const data: any = await res.json().catch(() => null);
    const raw = data?.choices?.[0]?.message?.content ?? "{}";
    let parsed: any = {};
    try { parsed = JSON.parse(raw); } catch {
      const s = String(raw).replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();
      const a = s.indexOf("{"); const b = s.lastIndexOf("}");
      if (a >= 0 && b > a) { try { parsed = JSON.parse(s.slice(a, b + 1)); } catch {} }
    }
    const clean = (v: any) =>
      typeof v === "string" && v.trim() && v.trim().toLowerCase() !== "unknown"
        ? v.trim()
        : undefined;
    return {
      title: clean(parsed.title),
      company: clean(parsed.company),
      city: clean(parsed.city),
      oneLiner: clean(parsed.oneLiner),
    };
  } catch (e) {
    console.error("aiExtractPreview failed", e);
    return {};
  }
}

export const previewEnrichPerson = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        name: z.string().min(1).max(200),
        company: z.string().max(200).optional().nullable(),
        email: z.string().max(320).optional().nullable(),
        hint: z.string().max(500).optional().nullable(),
      })
      .parse(i),
  )
  .handler(async ({ data }) => {
    const name = data.name.trim();
    if (name.length < 2) {
      return { status: "empty" as const, confidence: 0 };
    }
    const company = data.company?.trim() || null;
    const emailDomain = (() => {
      const e = data.email?.trim().toLowerCase();
      if (!e) return null;
      const d = e.split("@")[1];
      if (!d) return null;
      if (GENERIC_MAILBOX.has(d)) return null;
      return d;
    })();
    const companyHint = inferCompanyHint(name, company);
    const searchName = stripTrailingCompany(name, company ? null : companyHint);
    const queries = buildSearchQueries(searchName, companyHint, emailDomain);
    if (!queries.includes(searchName)) queries.push(searchName);

    const searchResults = await Promise.allSettled(
      queries.slice(0, 3).map((q) => searchFirecrawl(q, 5)),
    );
    const seenUrls = new Set<string>();
    const uniqHits = searchResults
      .flatMap((r) => r.status === "fulfilled" ? r.value : [])
      .filter((h) => seenUrls.has(h.url) ? false : (seenUrls.add(h.url), true));

    if (uniqHits.length === 0) {
      return { status: "empty" as const, confidence: 0 };
    }

    const scored = uniqHits
      .map((h) => scoreHit(h, { name: searchName, emailDomain, company: companyHint }))
      .filter((c): c is Candidate => !!c);
    const top = pickTopByKind(scored);
    let confidence = Math.min(1, top.reduce((s, c) => s + c.score, 0));

    // Hard company gate (preview): same as autoDiscoverContact — if the user
    // typed a company, refuse to surface a "found" answer unless at least one
    // top hit actually mentions it. Avoids the Add People review screen
    // auto-filling the wrong title/company/city.
    if (company) {
      const anyMentions = top.some((c) => {
        const hit = uniqHits.find((h) => h.url === c.url);
        const blob = `${hit?.title ?? ""} ${hit?.description ?? ""}`;
        const host = safeHost(c.url) ?? "";
        return blobMentionsCompany(blob, host, company);
      });
      if (!anyMentions) {
        console.info("preview-enrich: company gate triggered", { name: searchName, company });
        return { status: "empty" as const, confidence: Math.min(confidence, 0.5) };
      }
    }

    if (top.length === 0 || confidence < 0.35) {
      return { status: "empty" as const, confidence };
    }

    const extracted = await aiExtractPreview(searchName, uniqHits, { company: companyHint, emailDomain });
    const best = top[0];

    // Snippet fallback: LinkedIn's search title carries the role even when the
    // model can't extract one from the page text we're allowed to read.
    let title = extracted.title;
    let companyOut = extracted.company;
    if (!title || !companyOut) {
      try {
        const resolved = await resolveProfile({
          name: searchName,
          company,
          emailDomain,
          hits: uniqHits,
        });
        title = title ?? resolved.title ?? undefined;
        companyOut = companyOut ?? resolved.company ?? undefined;
      } catch (e) {
        console.error("preview resolveProfile failed", e);
      }
    }

    return {
      status: "found" as const,
      confidence,
      source: { kind: best.kind, url: best.url },
      title,
      company: companyOut,
      city: extracted.city,
      oneLiner: extracted.oneLiner,
    };
  });